/**
 * Active A/B testing experiments.
 */
export enum Experiment {
  summary_page_copy_relaunch_4113 = 'summary_page_copy_relaunch_4113',
  design_v4_4359 = 'design_v4_4359',
  ai_label_13537 = 'ai_label_13537',
  // Used just as a feature flag. No activation of the experiment on the backend.
  google_billing_7517 = 'google_billing_7517',
  // Experiment to be only used in tests
  test_experiment = 'test_experiment',
  backlink_8567 = 'backlink_8567',
  new_copy_pdf_v2_13448 = 'new_copy_pdf_v2_13448',
  exp_audio_highlight_982 = 'exp_audio_highlight_982',
  ai_guide_flow_13747 = 'ai_guide_flow_13747',
  bookmarks_14994 = 'bookmarks_14994',
  exp_ios_external_link_payment_16218 = 'exp_ios_external_link_payment_16218',
  exp_null_experiment_17503 = 'exp_null_experiment_17503',
  exp_first_guide_activation_17817 = 'exp_first_guide_activation_17817',
  exp_time_to_read_17958 = 'exp_time_to_read_17958',
  exp_free_user_upgrade_18151 = 'exp_free_user_upgrade_18151',
  exp_passwordless_signup_18304 = 'exp_passwordless_signup_18304',
  exp_onboarding_v9_entry_personalization_18361 = 'exp_onboarding_v9_entry_personalization_18361',
  exp_billing_trial_annual_upsell_18236 = 'exp_billing_trial_annual_upsell_18236',
  exp_trial_by_plan_v2_18369 = 'exp_trial_by_plan_v2_18369',
  exp_homepage_hero_search_18446 = 'exp_homepage_hero_search_18446',
  exp_cancel_save_flow_17977 = 'exp_cancel_save_flow_17977',
  exp_free_botd_18484 = 'exp_free_botd_18484',
  exp_summaries_page_redesign_18591 = 'exp_summaries_page_redesign_18591',
}

/**
 * Hint for the checkResponse method.
 */
export enum CheckResponseHint {
  register = 'register',
  login = 'login',
  passwordReset = 'passwordReset',
  abStartError = 'abStartError',
  updateUserData16218 = 'updateUserData',
}

/**
 * A type definition for the dictionary of experiments and assigned variants:
 * {
 *      experiment_X: 'variant1',
 *      site_page_1208: 'variant2',
 *  }
 */
export type Experiments = { [Key in Experiment]: string | null }

/**
 * A/B experiment and assigned variant data returned by the backend.
 */
export interface ABVariantData {
  experiment: Experiment
  variant: string
  client_id: string
  config?: any
}

export type ExperimentsDict = { [experiment: string]: string }

/**
 * A/B experiments data - client id and all active experiments for the user.
 */
export interface ABExperimentsData {
  client_id: string | null
  experiments: ExperimentsDict
}

/**
 * The backend response can contain a side-loaded
 * A/B testing information:
 * {
 *   "data": { ... regular response ... },
 *   "ab": { ... experiments / assigned variants data ... }
 *  }
 *  This way we can start experiments without sending additional
 *  API request to /ab/start endpoint.
 */
export interface ABSideloadedResponse {
  ab: ABExperimentsData
}

/**
 * EventData is the general A/B testing event information.
 *
 * We include current `url`, the `referrer` we came from,
 * client's `platform` and `os`.
 *
 * The `client_id` is optional, we may or may not have set
 * it yet on the client side.
 *
 * The `experiments` is an optional array of the A/B testing
 * experiments already active on the client.
 */
export type EventData = {
  client_id: string | null
  url: string
  referrer: string
  platform: string
  os: string
  mixpanel_device_id?: string
  experiments?: Experiments
}

/**
 * StartEvent represents the data for the /ab/start call.
 *
 * Besides the general data about the event from `EventData`,
 * we also have `experiment` here - the experiment to start.
 *
 * There is no `event` (it is set to "start" on the server)
 * and no `variant` (it is not assigned yet, server will return it).
 */
export interface StartEvent extends EventData {
  experiment: string
  variant: string | null
}

/**
 * ConvertEvent represents the data for the /ab/event call.
 *
 * We may have many conversion events for the same experiment.
 * The `event` is the event name ('signup', 'card save', etc).
 *
 * The `variant` is the value that was assigned to the client
 * previously and `experiment` is the experiment name we send
 * the conversion `event` for.
 */
export interface ConvertEvent extends EventData {
  event: string
  variant: string
  experiment: string
}

export type AudioHighlight982Variant = 'A' | 'B' | null
export type TrialByPlanV2_18369Variant = 'A' | 'B' | null
export type IosExternalLinkPayment16218Variant = 'A' | 'B' | null
export type NullExperiment17503Variant = 'A' | 'B' | null
export type FirstGuideActivation17817Variant = 'A' | 'B' | null
export type TimeToRead17958Variant = 'A' | 'B' | null
export type FreeUserUpgrade18151Variant = 'A' | 'B' | '_EXCLUDED_' | null
export type PasswordlessSignup18304Variant = 'A' | 'B' | null
export type OnboardingV9EntryPersonalization18361Variant = 'A' | 'B' | null
export type BillingTrialAnnualUpsell18236Variant = 'A' | 'B' | null
export type HomepageHeroSearch18446Variant = 'A' | 'B' | null
export type CancelSaveFlow17977Variant = 'A' | 'B' | null
export type FreeBookOfTheDay18484Variant = 'A' | 'B' | null
